/**
 * Condition Template — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/condition-template.routes.ts`
 * imports these to register the route, and `apps/landlord/src/lib/condition.api.ts` derives
 * its template response/body types from the same objects. See packages/api-types/README.md.
 *
 * Manager-only — condition template (the persistent per-unit `UnitRoom`/`UnitRoomItem`
 * checklist, BR-1/BR-2/BR-3) has no tenant-facing route.
 *
 * Covers:
 *   GET    /landlord/api/condition/room-type-defaults
 *   GET    /landlord/api/units/:unitId/condition-template
 *   POST   /landlord/api/units/:unitId/condition-template/instantiate
 *   POST   /landlord/api/units/:unitId/condition-template/rooms
 *   PATCH  /landlord/api/condition-template/rooms/:roomId
 *   DELETE /landlord/api/condition-template/rooms/:roomId
 *   POST   /landlord/api/condition-template/rooms/:roomId/items
 *   PATCH  /landlord/api/condition-template/items/:itemId
 *   DELETE /landlord/api/condition-template/items/:itemId
 */
import { z } from 'zod';
// `ItemCategorySchema` is identical to (and re-exported alongside) the walkthrough domain's
// copy — hoisting both `condition-walkthrough.routes.ts` and `condition-template.routes.ts`
// into this one package surfaced a genuine collision `export *` can't resolve on its own, so
// this file imports the single source of truth instead of re-declaring it (the route files
// themselves duplicated it independently; the package doesn't have to).
import { ItemCategorySchema } from './walkthrough.js';
export { ItemCategorySchema };
// ============================================================================
// ENUMS — must match `packages/database/schema/condition.prisma` exactly.
// ============================================================================
export const RoomTypeSchema = z.enum([
    'BEDROOM',
    'BATHROOM',
    'KITCHEN',
    'LIVING_ROOM',
    'DINING_ROOM',
    'HALLWAY',
    'CLOSET',
    'GARAGE',
    'BALCONY_PATIO',
    'LAUNDRY_ROOM',
    'OTHER',
]);
// ============================================================================
// REQUEST
// ============================================================================
export const CreateRoomBodySchema = z.object({
    roomType: RoomTypeSchema,
    name: z.string().min(1).max(255),
    displayOrder: z.number().int().min(0).optional(),
    populateDefaults: z.boolean().optional(),
});
export const UpdateRoomBodySchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    displayOrder: z.number().int().min(0).optional(),
})
    .refine((data) => Object.keys(data).length > 0, { message: 'At least one field must be provided for update' });
export const CreateItemBodySchema = z.object({
    name: z.string().min(1).max(255),
    category: ItemCategorySchema,
    make: z.string().max(255).nullable().optional(),
    model: z.string().max(255).nullable().optional(),
    serialNumber: z.string().max(255).nullable().optional(),
    displayOrder: z.number().int().min(0).optional(),
});
export const UpdateItemBodySchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    category: ItemCategorySchema.optional(),
    make: z.string().max(255).nullable().optional(),
    model: z.string().max(255).nullable().optional(),
    serialNumber: z.string().max(255).nullable().optional(),
    displayOrder: z.number().int().min(0).optional(),
})
    .refine((data) => Object.keys(data).length > 0, { message: 'At least one field must be provided for update' });
// ============================================================================
// RESPONSE
// ============================================================================
export const UnitRoomItemResponseSchema = z.object({
    id: z.uuid(),
    roomId: z.uuid(),
    name: z.string(),
    category: ItemCategorySchema,
    make: z.string().nullable(),
    model: z.string().nullable(),
    serialNumber: z.string().nullable(),
    displayOrder: z.number(),
    deletedAt: z.string().nullable(),
});
export const UnitRoomResponseSchema = z.object({
    id: z.uuid(),
    unitId: z.uuid(),
    roomType: RoomTypeSchema,
    name: z.string(),
    displayOrder: z.number(),
    deletedAt: z.string().nullable(),
    items: z.array(UnitRoomItemResponseSchema),
});
/** A single room-type default catalog entry (DC-1's static seed data, one per `RoomType`). */
export const RoomTypeDefaultSchema = z.object({
    roomType: RoomTypeSchema,
    defaultItems: z.array(z.object({ name: z.string(), category: ItemCategorySchema })),
});
export const RoomTypeDefaultsResponseSchema = z.object({
    defaults: z.array(RoomTypeDefaultSchema),
});
export const ListTemplateResponseSchema = z.object({ rooms: z.array(UnitRoomResponseSchema) });
export const RoomResponseSchema = z.object({ room: UnitRoomResponseSchema });
export const ItemResponseSchema = z.object({ item: UnitRoomItemResponseSchema });
//# sourceMappingURL=template.js.map